import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  NotificationCenter,
  useNotifications,
  useMarkAsRead,
  useUnreadNotificationsCount,
  NotificationService,
} from "@project-mgmt/feature-notifications";
import { Container, Page, Card, Button } from "@project-mgmt/ui";
import { routes } from "../utils/routes";
import { EmptyState } from "../components/ErrorState";
import type { WorkspaceRouteParams } from "../types/routes";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";

export function NotificationsPage() {
  // Render tracking
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[RENDER TRACKING] NotificationsPage rendered (count: ${renderCount.current})`, {
      timestamp: new Date().toISOString(),
    });
  });

  const navigate = useNavigate();
  const { workspaceId } = useParams<WorkspaceRouteParams>();
  const [filter, setFilter] = useState<"all" | "unread">("all");

  // Fetch workspace details for display
  const { data: workspaceData } = useWorkspaceDetails(workspaceId ?? "");
  const workspace = workspaceData?.workspace;

  // Fetch notifications
  const {
    data: notifications,
    isLoading,
    error,
    refetch: refetchNotifications,
  } = useNotifications(workspaceId ?? "");

  // Get unread count
  const { data: unreadData } = useUnreadNotificationsCount(workspaceId);
  const unreadCount = unreadData?.count ?? 0;

  // Mark as read hook
  const { markAllAsRead, isLoading: isMarkingAllRead } = useMarkAsRead(workspaceId);

  if (!workspaceId) {
    return <EmptyState message="Workspace ID not found." />;
  }

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      await refetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  // Filter notifications based on selected filter
  const filteredNotifications =
    filter === "unread" ? (notifications?.filter((n) => !n.readAt) ?? []) : (notifications ?? []);

  // Get notification summary
  const summary = notifications ? NotificationService.getNotificationSummary(notifications) : null;

  return (
    <Container size="xl">
      <Page>
        {/* Header with Actions */}
        <Card className="mb-6 p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-textPrimary mb-2 text-2xl font-bold">Notifications</h1>
              {workspace && (
                <p className="text-textSecondary text-sm">
                  Workspace: <span className="text-textPrimary font-medium">{workspace.name}</span>
                </p>
              )}
              {summary && (
                <div className="mt-2 flex flex-wrap gap-3 text-sm">
                  <span className="text-textSecondary">
                    Total: <span className="text-textPrimary font-medium">{summary.totalCount}</span>
                  </span>
                  <span className="text-textSecondary">
                    Unread: <span className="text-textPrimary font-medium">{summary.unreadCount}</span>
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAllRead}
                  className="touch-target"
                >
                  {isMarkingAllRead ? "Marking..." : "Mark All Read"}
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Filter Tabs */}
        <Card className="mb-6 p-4">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`touch-target rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                filter === "all" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All ({notifications?.length ?? 0})
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`touch-target rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                filter === "unread" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>
        </Card>

        {/* Notifications List */}
        <Card className="p-6">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
              </div>
              <p className="text-textSecondary">Loading notifications...</p>
            </div>
          ) : error ? (
            <EmptyState message={`Failed to load notifications: ${error.message}`} />
          ) : filteredNotifications.length === 0 ? (
            <EmptyState
              message={
                filter === "unread"
                  ? "No unread notifications"
                  : "No notifications yet. You'll see updates about tasks, projects, and workspace activities here."
              }
            />
          ) : (
            <NotificationCenter
              workspaceId={workspaceId}
              maxHeight="max-h-[calc(100vh-400px)]"
              onNotificationClick={(notification) => {
                // Navigate to related resource if available
                if (notification.relatedId) {
                  // You can add navigation logic here based on notification type
                  console.log("Navigate to:", notification.type, notification.relatedId);
                }
              }}
            />
          )}
        </Card>

        {/* Quick Actions */}
        {notifications && notifications.length > 0 && (
          <Card className="mt-6 p-6">
            <h2 className="text-textPrimary mb-4 text-lg font-semibold">Quick Actions</h2>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate(routes.workspace(workspaceId))}>
                Back to Workspace
              </Button>
              <Button variant="outline" onClick={() => navigate(routes.workspaceTasks(workspaceId))}>
                View Tasks
              </Button>
              <Button variant="outline" onClick={() => navigate(routes.projects(workspaceId))}>
                View Projects
              </Button>
            </div>
          </Card>
        )}
      </Page>
    </Container>
  );
}
